import { Controller } from '@hotwired/stimulus'

interface TurboFrameElement extends HTMLElement {
  src: string | null
  reload(): void
}

// Couples the "N ballots submitted" headline to the question frames: when a
// question frame anywhere on the page loads READY results, the headline frame
// reloads right after so both regions flip together. Listens on document —
// question frames are never ancestors of the headline (the AG-Grid layout
// renders it inside ResultHeaderComponent). The controller lives inside the
// frame content and reconnects on every reload, so the throttle timestamp
// lives on the OUTER frame's dataset (like the poller's warmPollAttempt) to
// survive content replacement.
export default class extends Controller<HTMLElement> {
  static values = {
    minInterval: { type: Number, default: 10000 },
  }

  declare readonly minIntervalValue: number

  private onFrameLoad = (event: Event): void => this.frameLoaded(event)

  connect(): void {
    document.addEventListener('turbo:frame-load', this.onFrameLoad)
  }

  disconnect(): void {
    document.removeEventListener('turbo:frame-load', this.onFrameLoad)
  }

  private frameLoaded(event: Event): void {
    const frame = this.frame()
    if (!frame || !frame.src) return

    const loaded = event.target as HTMLElement | null
    if (!loaded || loaded === frame) return
    // Only READY question content (admin + public fragments mark it); skips
    // warming skeletons, the tally-status (i) frame, and the headline itself.
    if (!loaded.querySelector('[data-results-ready]')) return

    const now = Date.now()
    if (now - Number(frame.dataset.overviewSyncedAt || 0) < this.minIntervalValue) return

    frame.dataset.overviewSyncedAt = String(now)
    frame.reload()
  }

  private frame(): TurboFrameElement | null {
    return this.element.closest('turbo-frame') as TurboFrameElement | null
  }
}
